import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useCallback, useContext, useEffect, useId, useRef, useState } from "preact/hooks";

interface ToastMessage {
  id: number;
  text: string;
  tone: "info" | "error" | "success";
}

type ToastApi = (text: string, tone?: ToastMessage["tone"]) => void;

const ToastContext = createContext<ToastApi>(() => undefined);

export function ToastProvider({ children }: { children: ComponentChildren }) {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const timeout = useRef<number | null>(null);
  const show = useCallback<ToastApi>((text, tone = "info") => {
    if (timeout.current !== null) window.clearTimeout(timeout.current);
    setMessage({ id: Date.now(), text, tone });
    timeout.current = window.setTimeout(() => setMessage(null), tone === "error" ? 8_000 : 4_000);
  }, []);

  useEffect(() => () => {
    if (timeout.current !== null) window.clearTimeout(timeout.current);
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div class="toast-region" aria-live="polite" aria-atomic="true">
        {message && (
          <div class={`app-toast app-toast-${message.tone}`} role={message.tone === "error" ? "alert" : "status"}>
            <span>{message.text}</span>
            <button type="button" aria-label="关闭通知" onClick={() => setMessage(null)}>×</button>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

const DIALOG_FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "audio[controls]",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function dialogFocusableElements(dialog: HTMLDialogElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE))
    .filter((element) => element.getAttribute("aria-hidden") !== "true");
}

export function Dialog({
  title,
  open,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ComponentChildren;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const invoker = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      invoker.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
      window.requestAnimationFrame(() => {
        const initial = dialog.querySelector<HTMLElement>("[data-dialog-initial]")
          ?? dialog.querySelector<HTMLElement>(DIALOG_FOCUSABLE);
        initial?.focus();
      });
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const close = (): void => {
    onClose();
    window.requestAnimationFrame(() => invoker.current?.focus());
  };

  const trapFocus = (event: KeyboardEvent): void => {
    if (event.key !== "Tab") return;
    const dialog = ref.current;
    if (!dialog) return;
    const focusable = dialogFocusableElements(dialog);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <dialog
      ref={ref}
      class={`app-dialog${wide ? " app-dialog-wide" : ""}`}
      aria-labelledby={titleId}
      aria-modal="true"
      onKeyDown={trapFocus}
      onCancel={(event) => { event.preventDefault(); close(); }}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <div class="dialog-surface">
        <header class="dialog-header">
          <h2 id={titleId} tabIndex={-1}>{title}</h2>
          <button type="button" class="icon-button" aria-label={`关闭${title}`} onClick={close}>×</button>
        </header>
        {children}
      </div>
    </dialog>
  );
}

export function LoadingState({ label = "加载中…" }: { label?: string }) {
  return <div class="state-panel" role="status" aria-live="polite"><span class="spinner" aria-hidden="true" />{label}</div>;
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const text = error instanceof Error ? error.message : "发生未知错误";
  return (
    <div class="state-panel state-error" role="alert">
      <strong>加载失败</strong>
      <span>{text}</span>
      {retry && <button type="button" class="btn btn-ghost" onClick={retry}>重试</button>}
    </div>
  );
}

export function SecretInput({
  label,
  configured,
  value,
  onInput,
  onClear,
  placeholder = "留空则保留现有密钥",
  clearConfirmation,
}: {
  label: string;
  configured: boolean;
  value: string;
  onInput: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  clearConfirmation?: string;
}) {
  const id = useId();
  const [editing, setEditing] = useState(!configured);
  useEffect(() => {
    if (!configured) setEditing(true);
  }, [configured]);
  if (configured && !editing) {
    return (
      <div class="secret-field protected-secret" role="group" aria-label={label}>
        <div class="protected-secret-status">
          <span>{label}</span><span class="configured-mark">已配置</span>
        </div>
        <div class="actions">
          <button type="button" class="btn btn-ghost" onClick={() => setEditing(true)}>更换</button>
          {onClear && <button type="button" class="btn btn-ghost" onClick={() => {
            if (window.confirm(clearConfirmation ?? `清除${label}？依赖该密钥的云端功能将暂停。`)) onClear();
          }}>清除</button>}
        </div>
      </div>
    );
  }
  return (
    <div class="secret-field">
      <label for={id}>
        <span>{label} {configured && <span class="configured-mark">已配置</span>}</span>
        <input
          id={id}
          type="password"
          value={value}
          autoComplete="new-password"
          placeholder={configured ? placeholder : "尚未配置"}
          onInput={(event) => onInput(event.currentTarget.value)}
        />
      </label>
      {configured && <button type="button" class="btn btn-ghost" onClick={() => {
        onInput(""); setEditing(false);
      }}>取消更换</button>}
    </div>
  );
}
